import { type Task, type Milestone } from '../packages/api-client/pm-api';
import { type Phase } from '../packages/api-client/plan-api';

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// ── Row model (shared by the chart and its table alternative) ──────────────
export interface GanttRow {
  id: string;
  label: string;
  /** Raw ISO dates as the API returned them (the table shows these). */
  start?: string;
  due?: string;
  startDate?: Date;
  dueDate?: Date;
  color: string;
  isMilestone?: boolean;
  phaseId?: string;
  critical?: boolean;
}

export type GanttItem =
  | { type: 'row'; row: GanttRow }
  | { type: 'header'; label: string; color?: string; phaseId?: string };

/**
 * The chart's rows in display order: milestones first, then tasks grouped by
 * phase (by position) with an "Unphased" group, or flat when there are no
 * phases. Only dated items appear. GanttChart and GanttTable both render from
 * this, so the table alternative always carries exactly the chart's rows.
 */
export function buildGanttItems(tasks: Task[], milestones: Milestone[], phases?: Phase[], criticalTaskIds?: Set<string>): GanttItem[] {
  const taskRows: GanttRow[] = tasks
    .filter(t => t.start_date || t.due_date)
    .map(t => ({
      id: t.id,
      label: t.title,
      start: t.start_date,
      due: t.due_date,
      startDate: t.start_date
        ? new Date(t.start_date)
        : t.due_date ? addDays(new Date(t.due_date), -1) : undefined,
      dueDate: t.due_date ? new Date(t.due_date) : undefined,
      color: 'var(--accent)',
      phaseId: t.phase_id,
      critical: criticalTaskIds?.has(t.id) ?? false,
    }));

  const msRows: GanttRow[] = milestones
    .filter(m => m.due_date)
    .map(m => ({
      id: m.id,
      label: m.name,
      due: m.due_date,
      startDate: new Date(m.due_date!),
      dueDate: new Date(m.due_date!),
      color: 'var(--warn)',
      isMilestone: true,
    }));

  if (!phases?.length) {
    return [...msRows, ...taskRows].map(row => ({ type: 'row' as const, row }));
  }

  const items: GanttItem[] = [];
  if (msRows.length > 0) {
    items.push({ type: 'header', label: 'Milestones' });
    msRows.forEach(row => items.push({ type: 'row', row }));
  }
  const sortedPhases = [...phases].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const phaseIds = new Set(sortedPhases.map(p => p.id));
  sortedPhases.forEach(phase => {
    const phaseTasks = taskRows.filter(r => r.phaseId === phase.id);
    if (phaseTasks.length === 0) return;
    items.push({ type: 'header', label: phase.name, color: phase.color, phaseId: phase.id });
    phaseTasks.forEach(row => items.push({ type: 'row', row }));
  });
  const unphased = taskRows.filter(r => !r.phaseId || !phaseIds.has(r.phaseId));
  if (unphased.length > 0) {
    items.push({ type: 'header', label: 'Unphased' });
    unphased.forEach(row => items.push({ type: 'row', row }));
  }
  return items;
}
